import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import Tooltip from '@/components/core/Tooltip';
import { parseErrorMessage } from '@/utils/errorUtils';

// the API decides which settings exist; these just name the ones we know
const ROW_LABELS = {
  emissions: 'Emissions (CO₂e)',
  mass: 'Mass',
  energy: 'Energy',
};

const COLUMN_LABELS = {
  absolute: 'Absolute',
  levelized: 'Levelized',
};

const TOOLTIP_TEXT = `**Absolute** is for annual amounts: the flows in the M&E Balance and total emissions per year.

**Levelized** is for results per unit of product. The emissions setting is the top of an emissions intensity and the mass or energy setting is the bottom, so together they set its units: kg CO₂e / ton is 1,000 times ton CO₂e / ton.

Electricity keeps its own units (MW, and per kWh), and costs stay in $. New projects start with their creator's export units from account settings.`;

const labelFor = (labels, key) => labels[key] ?? key.charAt(0).toUpperCase() + key.slice(1);

const unique = values => [...new Set(values)];

const ExportUnitsGrid = ({ caption, settings, onSave }) => {
  const [savingName, setSavingName] = useState(null);

  const rows = unique(settings.map(setting => setting.row));
  const columns = unique(settings.map(setting => setting.column));

  const onChange = async (setting, unit) => {
    setSavingName(setting.name);

    try {
      await onSave(setting.name, unit);
    } catch (error) {
      parseErrorMessage(error).forEach(message => toast.error(message));
    } finally {
      setSavingName(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 text-sm text-gray-500">
        <p>{caption}</p>
        <Tooltip label="Export Units" text={TOOLTIP_TEXT} />
      </div>
      <div
        className="grid items-center gap-x-4 gap-y-2"
        style={{ gridTemplateColumns: `auto repeat(${columns.length}, minmax(0, 1fr))` }}
      >
        <div />
        {columns.map(column => (
          <div key={column} className="text-sm font-medium">
            {labelFor(COLUMN_LABELS, column)}
          </div>
        ))}
        {rows.map(row => (
          <Fragment key={row}>
            <div className="text-sm">{labelFor(ROW_LABELS, row)}</div>
            {columns.map(column => {
              const setting = settings.find(item => item.row === row && item.column === column);

              if (!setting) {
                return <div key={column} />;
              }

              return (
                <select
                  key={column}
                  aria-label={`${labelFor(ROW_LABELS, row)} ${labelFor(COLUMN_LABELS, column).toLowerCase()}`}
                  value={setting.value}
                  onChange={event => onChange(setting, event.target.value)}
                  disabled={savingName === setting.name}
                  className="select select-sm select-bordered w-full"
                >
                  {setting.options.map(option => (
                    <option key={option.name} value={option.name}>
                      {option.name === setting.default ? `${option.label} (default)` : option.label}
                    </option>
                  ))}
                </select>
              );
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
};

ExportUnitsGrid.propTypes = {
  caption: PropTypes.string.isRequired,
  settings: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      row: PropTypes.string.isRequired,
      column: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      default: PropTypes.string.isRequired,
      options: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, label: PropTypes.string })).isRequired,
    }),
  ).isRequired,
  // saves one setting; rejects with the API error when it can't
  onSave: PropTypes.func.isRequired,
};

export default ExportUnitsGrid;
